import { useCallback, useMemo, useState } from 'react';
import { useMutation } from 'data-fetching-client';
import { useSelector } from 'react-redux';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { ListItem, List } from 'foundations-components/transitional/components/List';
import { Section } from 'foundations-components/transitional/components/Section';
import { Button } from 'foundations-components/transitional/components/Button';
import Rating from 'marketplace-ui-reviews/components/Rating';
import { Reportables } from 'marketplace-ui-reviews/constants/ReportReasons';
import { RatingAxes } from 'marketplace-ui-reviews/constants/RatingAxes';
import HelpfulButton from 'marketplace-ui-reviews/components/HelpfulButton';
import ReviewActionsWrapper from 'marketplace-ui-reviews/components/ReviewActionsWrapper';
import ReportButton from 'marketplace-ui-reviews/components/ReportButton';
import ReviewTranslationToggle from 'marketplace-ui-reviews/components/ReviewTranslationToggle';
import ReviewBody from 'marketplace-ui-reviews/components/ReviewBody';
import ReviewerInfo from 'marketplace-ui-reviews/components/ReviewerInfo';
import ReviewReplyThread from 'marketplace-ui-reviews/components/ReviewReplyThread';
import ReportReviewModal from 'marketplace-ui-reviews/components/modals/ReportReviewModal';
import { REPORT_REVIEW } from 'marketplace-ui-reviews/queries/reportReviewQuery';
import { REPORT_REPLY } from 'marketplace-ui-reviews/queries/reportReplyQuery';
import { SET_REVIEW_HELPFUL } from 'marketplace-ui-reviews/queries/setReviewHelpfulQuery';
import { getUserMarkedHelpful } from 'marketplace-ui-reviews/utils/getUserMarkedHelpful';
import { getIsCustomer, getUserId } from '../../selectors/auth';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default function AssetReviewRenderer({
  borderBottom,
  review,
  searchTerm,
  reviewType,
  onTrackInteraction
}) {
  const [reportModalType, setReportModalType] = useState(null);
  const isCustomer = useSelector(getIsCustomer);
  const userId = useSelector(getUserId);
  const userMarkedHelpful = getUserMarkedHelpful(userId, review);
  const [localMarkedHelpful, setUserMarkedHelpful] = useState(userMarkedHelpful);
  const [showOriginal, setShowOriginal] = useState(false);
  const [showUntranslatedText, setShowUntranslatedText] = useState(false);
  const [setIsHelpful] = useMutation(SET_REVIEW_HELPFUL);
  const [reportReview] = useMutation(REPORT_REVIEW);
  const [reportReply] = useMutation(REPORT_REPLY);
  const ratingValue = showOriginal ? review.get('original').ratings.get(RatingAxes.OVERALL) : review.getIn(['ratings', RatingAxes.OVERALL]);
  const handleClickHelpful = useCallback(() => {
    return new Promise((resolve, reject) => {
      setIsHelpful({
        variables: {
          reviewId: review.get('id'),
          isHelpful: !localMarkedHelpful
        }
      }).then(() => {
        setUserMarkedHelpful(!localMarkedHelpful);
        resolve();
      }).catch(err => {
        reject(err);
      });
    });
  }, [review, setIsHelpful, localMarkedHelpful]);
  const renderedReportModal = useMemo(() => {
    const onConfirmAction = (reportReason, comment) => reportModalType === Reportables.REVIEW ? reportReview({
      variables: {
        reviewId: review.get('id'),
        reportReason,
        comment
      }
    }) : reportReply({
      variables: {
        reviewId: review.get('id'),
        replyId: review.getIn(['reply', 'id']),
        reportReason,
        comment
      }
    });
    const handleReportModalConfirm = ({
      reason,
      comment
    }) => {
      return new Promise((resolve, reject) => {
        onConfirmAction(reason, comment).then(() => {
          resolve();
          setReportModalType(null);
        }).catch(err => {
          reject(err);
        });
      });
    };
    return /*#__PURE__*/_jsx(ReportReviewModal, {
      review: review,
      onClose: () => {
        setReportModalType(null);
      },
      onConfirm: handleReportModalConfirm
    });
  }, [reportModalType, reportReply, reportReview, review]);
  const helpfulCount = useMemo(() => {
    const currentHelpfulCount = review.getHelpfulCount();
    if (userMarkedHelpful && !localMarkedHelpful) {
      return currentHelpfulCount > 0 ? currentHelpfulCount - 1 : 0;
    }
    if (!userMarkedHelpful && localMarkedHelpful) {
      return currentHelpfulCount + 1;
    }
    return currentHelpfulCount;
  }, [localMarkedHelpful, review, userMarkedHelpful]);
  const reviewActions = useMemo(() => {
    return /*#__PURE__*/_jsxs(ReviewActionsWrapper, {
      itemRight: /*#__PURE__*/_jsx(ReviewTranslationToggle, {
        review: review,
        onTrackInteraction: onTrackInteraction,
        showUntranslatedText: showUntranslatedText,
        setShowUntranslatedText: setShowUntranslatedText
      }),
      children: [/*#__PURE__*/_jsx(ListItem, {
        children: /*#__PURE__*/_jsx(HelpfulButton, {
          disabled: !isCustomer,
          helpfulCount: helpfulCount,
          onClickHelpful: handleClickHelpful,
          userMarkedHelpful: localMarkedHelpful
        })
      }), /*#__PURE__*/_jsx(ListItem, {
        children: /*#__PURE__*/_jsx(ReportButton, {
          isCustomer: isCustomer,
          onClick: () => setReportModalType(Reportables.REVIEW)
        })
      }), review.get('lastEditedAt') && review.get('original').ratings.get('OVERALL') !== review.get('ratings').get('OVERALL') && /*#__PURE__*/_jsx(ListItem, {
        children: /*#__PURE__*/_jsx(Button, {
          onClick: () => setShowOriginal(!showOriginal),
          use: "link",
          marginInlineStart: "200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `marketplace-assets-ui.themes.details.reviews.edit.${showOriginal ? 'showUpdated' : 'showOriginal'}`
          })
        })
      })]
    });
  }, [handleClickHelpful, helpfulCount, isCustomer, localMarkedHelpful, onTrackInteraction, review, showOriginal, showUntranslatedText]);
  return /*#__PURE__*/_jsxs(View, {
    as: "div",
    paddingBlock: "800",
    blockSize: "100%",
    borderBlockEndStyle: borderBottom ? 'solid' : undefined,
    borderBlockEndWidth: borderBottom ? '100' : undefined,
    borderBlockEndColor: borderBottom ? 'border-core-subtle' : undefined,
    children: [/*#__PURE__*/_jsx(Section, {
      children: /*#__PURE__*/_jsx(Rating, {
        direction: "column",
        label: /*#__PURE__*/_jsxs(List, {
          inline: true,
          use: "inline-divided",
          wrapChildren: true,
          children: [/*#__PURE__*/_jsx(FormattedDateTime, {
            value: review.get('createdAt'),
            format: "ll",
            type: "userTz"
          }), review.get('lastEditedAt') && /*#__PURE__*/_jsxs(View, {
            as: "span",
            marginInlineStart: "100",
            children: [/*#__PURE__*/_jsx(View, {
              as: "span",
              marginInlineEnd: "100",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "marketplace-assets-ui.themes.details.reviews.edit.edited"
              })
            }), /*#__PURE__*/_jsx(FormattedDateTime, {
              value: review.get('lastEditedAt'),
              format: "ll",
              type: "userTz"
            })]
          })]
        }),
        rating: ratingValue || 0,
        showTextRating: true
      })
    }), /*#__PURE__*/_jsx(ReviewBody, {
      review: review,
      searchTerm: searchTerm,
      showOriginal: showOriginal,
      showUntranslatedText: showUntranslatedText,
      reviewType: reviewType,
      onTrackInteraction: onTrackInteraction
    }), /*#__PURE__*/_jsx(ReviewerInfo, {
      review: review,
      inline: true
    }), reviewActions, /*#__PURE__*/_jsx(ReviewReplyThread, {
      reply: review.getReply(showUntranslatedText),
      onReportReplyClick: () => setReportModalType(Reportables.REPLY)
    }), reportModalType && renderedReportModal]
  });
}